import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import {
  MapPreviewFrame,
  MapStyleCreateSheetBody,
  MapStyleDependencyPanel,
  MapStyleDiagnosticsPanel,
  MapStyleInspectorWorkspace,
  MapStyleLibraryTable,
  MapStyleLibraryToolbar,
  MapStyleRuntimePanel,
  type MapAssetActionItem,
  type MapAssetStatItem,
  type MapStyleDependencySummaryData,
  type MapStyleLibraryRow,
  type MapStyleStatusBadge,
} from '@workspace/silk-ui';
import { Button, MapWidgetView, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@workspace/ui';

import { MappingAssetAccessSheet } from '@/components/mapping/MappingAssetAccessSheet';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import { useNavigation } from '@/context/NavigationContext';
import { useSession } from '@/context/SessionContext';
import {
  formatShareScopeLabel,
  isCustomMapAsset,
  isPortalAssetReadOnly,
  normalizeShareScope,
  parseGlyphSetIdFromStyleUrl,
  parseSpriteSetIdFromStyleUrl,
} from '@/lib/mapping-assets';
import { useMappingAccess } from '@/lib/useMappingAccess';
import {
  createMapStyle,
  deleteMapStyle,
  listGlyphSets,
  listMapSourceShares,
  listMapSources,
  listMapStyleShares,
  listMapStyles,
  listSpriteSets,
  previewMapStyle,
  replaceMapStyleShares,
  updateMapStyle,
  type GlyphSet,
  type MapSource,
  type MapStyle,
  type MapStyleCreateRequest,
  type MapStylePreviewResponse,
  type MapViewState,
  type ShareScope,
  type SpriteSet,
} from '@/lib/mapping-api';

const DEFAULT_PAGE_SIZE = 200;
const DEFAULT_VIEW_STATE: MapViewState = {
  center: [-84.5, 44.5],
  zoom: 7,
  bearing: 0,
  pitch: 0,
};

const STYLE_STARTERS: Record<string, Record<string, unknown>> = {
  blank: {
    version: 8,
    name: 'Blank Style',
    sources: {},
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: {
          'background-color': 'hsl(0 0% 98%)',
        },
      },
    ],
  },
  light: {
    version: 8,
    name: 'Light Analysis',
    sources: {},
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: {
          'background-color': 'hsl(210 20% 98%)',
        },
      },
    ],
  },
  dark: {
    version: 8,
    name: 'Dark Operations',
    sources: {},
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: {
          'background-color': 'hsl(224 33% 8%)',
        },
      },
    ],
  },
};

const STYLE_STARTER_OPTIONS = [
  {
    value: 'blank',
    label: 'Blank Canvas',
    description: 'Start with only a neutral background and add sources and layers in the workbench.',
  },
  {
    value: 'light',
    label: 'Light Analysis',
    description: 'A low-noise light base for operational overlays and annotations.',
  },
  {
    value: 'dark',
    label: 'Dark Operations',
    description: 'A darker shell for utility, telecom, and high-contrast monitoring maps.',
  },
] as const;

type MappingDependencyRef = {
  id?: string;
  name?: string;
  share_scope?: string | null;
};

type MappingSourceDependencyRef = MappingDependencyRef & {
  alias?: string;
};

type MapStyleMetadata = Record<string, unknown> & {
  source_refs?: MappingSourceDependencyRef[];
  sprite_ref?: MappingDependencyRef | null;
  glyph_ref?: MappingDependencyRef | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatBytes = (value?: number | null) => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const rounded = size >= 100 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
};

const countStyleLayers = (style?: MapStyle | null) => {
  const layers = style?.style_json?.layers;
  return Array.isArray(layers) ? layers.length : 0;
};

const countStyleSources = (style?: MapStyle | null) => {
  const metadataSources = (style?.metadata as MapStyleMetadata | undefined)?.source_refs;
  if (Array.isArray(metadataSources) && metadataSources.length > 0) {
    return metadataSources.length;
  }
  const sources =
    style?.style_json && typeof style.style_json === 'object'
      ? (style.style_json as Record<string, unknown>).sources
      : null;
  return sources && typeof sources === 'object' ? Object.keys(sources as Record<string, unknown>).length : 0;
};

const cloneStyleJson = (styleJson?: Record<string, unknown> | null) => {
  const source = styleJson ?? STYLE_STARTERS.blank;
  if (typeof structuredClone === 'function') {
    return structuredClone(source);
  }
  return JSON.parse(JSON.stringify(source)) as Record<string, unknown>;
};

const buildStarterStyleJson = (starterValue: string, name: string) => {
  const base = cloneStyleJson(STYLE_STARTERS[starterValue] ?? STYLE_STARTERS.blank);
  base.name = name || (typeof base.name === 'string' ? base.name : 'Untitled Style');
  return base;
};

const getStyleJsonSizeLabel = (styleJson?: Record<string, unknown> | null) => {
  if (!styleJson) return '—';
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(styleJson)).length;
    return formatBytes(bytes) ?? '—';
  } catch {
    return '—';
  }
};

const copyText = async (value: string) => {
  if (!value) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

const resolveMapRequestUrl = (url: string) => {
  if (!url) return url;
  const trimmed = url.trim();
  const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
  if (hasProtocol) return trimmed;
  const origin = typeof window !== 'undefined' ? window.location.origin : null;
  if (!origin) return trimmed;
  try {
    return new URL(trimmed, origin).toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
  } catch {
    return trimmed;
  }
};

const normalizeStyleUrls = (style: Record<string, unknown>) => {
  const clone = cloneStyleJson(style);
  const remap = (value?: string) => (typeof value === 'string' ? resolveMapRequestUrl(value) : value);

  if (typeof clone.sprite === 'string') {
    clone.sprite = remap(clone.sprite);
  }
  if (typeof clone.glyphs === 'string') {
    clone.glyphs = remap(clone.glyphs);
  }
  if (clone.sources && typeof clone.sources === 'object') {
    Object.values(clone.sources as Record<string, any>).forEach(source => {
      if (!source || typeof source !== 'object') return;
      if (Array.isArray(source.tiles)) {
        source.tiles = source.tiles.map((tile: string) => remap(tile));
      }
      if (typeof source.url === 'string') {
        source.url = remap(source.url);
      }
    });
  }

  return clone;
};

const extractPmtilesSourceId = (value: unknown) => {
  if (typeof value !== 'string' || !value.startsWith('pmtiles://')) {
    return null;
  }
  return value.slice('pmtiles://'.length);
};

const collectStyleSourceRefs = (styleJson?: Record<string, unknown> | null, sources: MapSource[] = []) => {
  const styleSources =
    styleJson?.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  return Object.entries(styleSources)
    .map(([alias, source]) => {
      const sourceId = extractPmtilesSourceId(source?.url);
      if (!sourceId) {
        return null;
      }
      const entry = sources.find(candidate => candidate.id === sourceId);
      return {
        id: sourceId,
        alias,
        name: entry?.name ?? sourceId,
        share_scope: normalizeShareScope(entry?.share_scope),
      } satisfies MappingSourceDependencyRef;
    })
    .filter((entry): entry is MappingSourceDependencyRef => Boolean(entry));
};

const normalizeSourceDependencyRefs = (sourceRefs: unknown, sources: MapSource[] = []) => {
  if (!Array.isArray(sourceRefs)) {
    return [];
  }

  return sourceRefs
    .map(entry => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const sourceId =
        (typeof record.id === 'string' && record.id.trim()) ||
        (typeof record.source_id === 'string' && record.source_id.trim()) ||
        '';
      if (!sourceId) {
        return null;
      }
      const source = sources.find(candidate => candidate.id === sourceId);
      return {
        id: sourceId,
        alias: typeof record.alias === 'string' ? record.alias : undefined,
        name:
          (typeof record.name === 'string' && record.name) ||
          (typeof record.source_name === 'string' && record.source_name) ||
          source?.name ||
          sourceId,
        share_scope: normalizeShareScope(
          (typeof record.share_scope === 'string' ? record.share_scope : null) ?? source?.share_scope,
        ),
      } satisfies MappingSourceDependencyRef;
    })
    .filter((entry): entry is MappingSourceDependencyRef => Boolean(entry));
};

const buildDependencySummary = (
  style: MapStyle,
  sources: MapSource[],
  spriteSets: SpriteSet[],
  glyphSets: GlyphSet[],
): MapStyleDependencySummaryData => {
  const metadata = (style.metadata as MapStyleMetadata | undefined) ?? null;
  const metadataSourceRefs = normalizeSourceDependencyRefs(metadata?.source_refs, sources);
  const sourceRefs = metadataSourceRefs.length > 0 ? metadataSourceRefs : collectStyleSourceRefs(style.style_json ?? null, sources);

  const sourceMap = new Map(sources.filter(source => source.id).map(source => [source.id as string, source]));
  const spriteMap = new Map(spriteSets.filter(sprite => sprite.id).map(sprite => [sprite.id as string, sprite]));
  const glyphMap = new Map(glyphSets.filter(glyph => glyph.id).map(glyph => [glyph.id as string, glyph]));

  const spriteId = metadata?.sprite_ref?.id ?? parseSpriteSetIdFromStyleUrl(style.style_json?.sprite);
  const glyphId = metadata?.glyph_ref?.id ?? parseGlyphSetIdFromStyleUrl(style.style_json?.glyphs);
  const sprite = spriteId ? spriteMap.get(spriteId) : null;
  const glyph = glyphId ? glyphMap.get(glyphId) : null;

  return {
    sources: sourceRefs.map(ref => {
      const source = sourceMap.get(ref.id as string);
      return {
        id: ref.id as string,
        label: source?.name ?? ref.name ?? (ref.id as string),
        alias: ref.alias ?? null,
        description: source?.description ?? null,
        shareScope: normalizeShareScope(source?.share_scope ?? ref.share_scope),
      };
    }),
    sprite: spriteId
      ? {
          id: spriteId,
          label: sprite?.name ?? metadata?.sprite_ref?.name ?? spriteId,
          description: sprite?.description ?? null,
          shareScope: normalizeShareScope(sprite?.share_scope ?? metadata?.sprite_ref?.share_scope),
        }
      : null,
    glyph: glyphId
      ? {
          id: glyphId,
          label: glyph?.name ?? metadata?.glyph_ref?.name ?? glyphId,
          description: glyph?.description ?? null,
          shareScope: normalizeShareScope(glyph?.share_scope ?? metadata?.glyph_ref?.share_scope),
        }
      : null,
  };
};

const countMissingDependencies = (
  dependencies: MapStyleDependencySummaryData,
  style: MapStyle,
  sources: MapSource[],
  spriteSets: SpriteSet[],
  glyphSets: GlyphSet[],
) => {
  const sourceIds = new Set(sources.map(source => source.id).filter((value): value is string => Boolean(value)));
  const spriteIds = new Set(spriteSets.map(sprite => sprite.id).filter((value): value is string => Boolean(value)));
  const glyphIds = new Set(glyphSets.map(glyph => glyph.id).filter((value): value is string => Boolean(value)));
  const metadata = (style.metadata as MapStyleMetadata | undefined) ?? null;

  let missingCount = dependencies.sources.filter(source => !sourceIds.has(source.id)).length;
  const spriteId = metadata?.sprite_ref?.id ?? parseSpriteSetIdFromStyleUrl(style.style_json?.sprite);
  const glyphId = metadata?.glyph_ref?.id ?? parseGlyphSetIdFromStyleUrl(style.style_json?.glyphs);
  if (spriteId && !spriteIds.has(spriteId)) {
    missingCount += 1;
  }
  if (glyphId && !glyphIds.has(glyphId)) {
    missingCount += 1;
  }
  return missingCount;
};

function StylePreviewMap({
  styleJson,
  viewState,
  resetToken,
}: {
  styleJson: Record<string, unknown> | null;
  viewState: MapViewState | null;
  resetToken: number;
}) {
  const resolvedView = viewState ?? DEFAULT_VIEW_STATE;

  return (
    <MapWidgetView
      key={`${resetToken}:${JSON.stringify(resolvedView)}`}
      className="h-full rounded-none border-0 bg-muted/20"
      styleJson={styleJson}
      status={styleJson ? 'success' : 'loading'}
      defaultViewState={resolvedView}
      inspectableLayerIds={[]}
      activeToolId="select"
      drawnGeometry={null}
    />
  );
}

export default function MapStylesPage() {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const mappingAccess = useMappingAccess();
  const canManageGlobal = session?.scopes?.includes('platform.mapping.manage') ?? false;

  const [shareScopeFilter, setShareScopeFilter] = useState<'all' | ShareScope>('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<MapStyle | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorTarget, setInspectorTarget] = useState<MapStyle | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createState, setCreateState] = useState({
    name: '',
    details: '',
    shareScope: (canManageGlobal ? 'public' : 'tenant') as ShareScope,
    starterValue: 'blank',
    advancedMode: false,
    jsonValue: JSON.stringify(STYLE_STARTERS.blank, null, 2),
  });
  const [inspectorStyleJson, setInspectorStyleJson] = useState<Record<string, unknown> | null>(null);
  const [inspectorViewState, setInspectorViewState] = useState<MapViewState | null>(null);
  const [inspectorStatus, setInspectorStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  const [previewResetToken, setPreviewResetToken] = useState(0);

  const { data, error, isLoading, mutate } = useSWR<MapStyle[]>(
    mappingAccess.accessEnabled ? ['portal-map-styles', shareScopeFilter] : null,
    () => listMapStyles({ limit: DEFAULT_PAGE_SIZE, share_scope: shareScopeFilter === 'all' ? undefined : shareScopeFilter }),
  );

  const { data: sourceCatalog = [] } = useSWR<MapSource[]>(
    mappingAccess.accessEnabled ? ['portal-map-style-sources'] : null,
    () => listMapSources({ limit: DEFAULT_PAGE_SIZE }),
  );

  const { data: spriteSets = [] } = useSWR<SpriteSet[]>(
    mappingAccess.accessEnabled ? ['portal-map-style-sprites'] : null,
    () => listSpriteSets({ limit: DEFAULT_PAGE_SIZE }),
  );

  const { data: glyphSets = [] } = useSWR<GlyphSet[]>(
    mappingAccess.accessEnabled ? ['portal-map-style-glyphs'] : null,
    () => listGlyphSets({ limit: DEFAULT_PAGE_SIZE }),
  );

  const filteredStyles = useMemo(() => {
    const styles = (data ?? []).filter(style => !isCustomMapAsset(style));
    const sourceScopedStyles =
      sourceFilter === 'all'
        ? styles
        : styles.filter(style =>
            normalizeSourceDependencyRefs((style.metadata as MapStyleMetadata | undefined)?.source_refs, sourceCatalog)
              .concat(collectStyleSourceRefs(style.style_json ?? null, sourceCatalog))
              .some(ref => ref.id === sourceFilter),
          );
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return sourceScopedStyles;
    }
    return sourceScopedStyles.filter(style =>
      [style.name, style.description, style.id]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(needle)),
    );
  }, [data, search, sourceCatalog, sourceFilter]);

  const sourceFilterOptions = useMemo(
    () => [
      {
        value: 'all',
        label: 'All Sources',
        description: 'Show every reusable style preset',
      },
      ...sourceCatalog
        .filter(source => source.id)
        .sort((left, right) => (left.name ?? left.id ?? '').localeCompare(right.name ?? right.id ?? ''))
        .map(source => ({
          value: source.id as string,
          label: source.name ?? (source.id as string),
          description: [
            Array.isArray(source.vector_layers) ? `${source.vector_layers.length} layers` : null,
            source.share_scope ? normalizeShareScope(source.share_scope) : null,
          ]
            .filter(Boolean)
            .join(' • '),
        })),
    ],
    [sourceCatalog],
  );

  const resetCreateForm = useCallback(() => {
    setCreateError(null);
    setSaving(false);
    setCreateState({
      name: '',
      details: '',
      shareScope: canManageGlobal ? 'public' : 'tenant',
      starterValue: 'blank',
      advancedMode: false,
      jsonValue: JSON.stringify(STYLE_STARTERS.blank, null, 2),
    });
  }, [canManageGlobal]);

  const handleCreate = async () => {
    setCreateError(null);
    setActionError(null);
    const name = createState.name.trim();
    if (!name) {
      setCreateError('Name is required.');
      return;
    }

    let styleJson: Record<string, unknown>;
    if (createState.advancedMode) {
      try {
        styleJson = JSON.parse(createState.jsonValue || '{}') as Record<string, unknown>;
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : 'Invalid JSON.');
        return;
      }
    } else {
      styleJson = buildStarterStyleJson(createState.starterValue, name);
    }

    setSaving(true);
    try {
      const payload: MapStyleCreateRequest = {
        name,
        description: createState.details.trim() || undefined,
        share_scope: createState.shareScope,
        style_json: styleJson,
      };
      const created = await createMapStyle(payload);
      await mutate();
      setSheetOpen(false);
      resetCreateForm();
      if (created?.id) {
        navigate(`/maps/styles/${created.id}/workbench`);
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create style');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (style: MapStyle) => {
    if (!style.id) return;
    if (!window.confirm(`Delete style "${style.name ?? style.id}"?`)) {
      return;
    }
    try {
      await deleteMapStyle(style.id);
      await mutate();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete style');
    }
  };

  const handleDuplicate = async (style: MapStyle) => {
    if (!style.style_json) return;
    try {
      const created = await createMapStyle({
        name: `Copy of ${style.name ?? 'Map Style'}`,
        description: style.description ?? undefined,
        share_scope: normalizeShareScope(style.share_scope, canManageGlobal ? 'public' : 'tenant'),
        style_json: cloneStyleJson(style.style_json),
        metadata: style.metadata ?? undefined,
      });
      await mutate();
      if (created?.id) {
        navigate(`/maps/styles/${created.id}/workbench`);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to duplicate style');
    }
  };

  const openInspector = useCallback(async (style: MapStyle) => {
    setInspectorOpen(true);
    setInspectorTarget(style);
    setInspectorStatus('loading');
    setInspectorError(null);
    setInspectorStyleJson(null);
    setInspectorViewState(null);

    const styleJson = cloneStyleJson(style.style_json);

    try {
      const payload = await previewMapStyle({
        style_json: styleJson,
        metadata: style.metadata ?? undefined,
      });
      const previewStyle = payload?.style_json ?? styleJson;
      setInspectorStyleJson(previewStyle ? normalizeStyleUrls(previewStyle) : null);
      setInspectorViewState(payload?.view_state ?? null);
      setInspectorStatus('success');
    } catch (err) {
      setInspectorStatus('error');
      setInspectorError(err instanceof Error ? err.message : 'Preview failed');
    }
  }, []);

  const styleRows = useMemo<MapStyleLibraryRow[]>(
    () =>
      filteredStyles.map(style => {
        const dependencies = buildDependencySummary(style, sourceCatalog, spriteSets, glyphSets);
        const missingCount = countMissingDependencies(dependencies, style, sourceCatalog, spriteSets, glyphSets);
        const layerCount = countStyleLayers(style);
        const sourceCount = countStyleSources(style);
        const readOnly = isPortalAssetReadOnly(style.tenant_id, mappingAccess.featureTenantId);

        const statusBadges: MapStyleStatusBadge[] = [
          {
            id: `${style.id}-state`,
            label: missingCount > 0 ? `${missingCount} missing` : 'Ready',
            tone: missingCount > 0 ? 'warning' : 'success',
          },
          ...(readOnly ? [{ id: `${style.id}-readonly`, label: 'Read-only', tone: 'warning' as const }] : []),
        ];

        const actions: MapAssetActionItem[] = [
          { id: 'workbench', label: 'Workbench' },
          { id: 'inspect', label: 'Inspect' },
          { id: 'duplicate', label: 'Duplicate' },
          { id: 'share', label: 'Access' },
          ...(readOnly ? [] : [{ id: 'delete', label: 'Delete', tone: 'danger' }]),
        ];

        const summaryItems: MapAssetStatItem[] = [
          { id: `${style.id}-size`, label: 'Style Size', value: getStyleJsonSizeLabel(style.style_json) },
          {
            id: `${style.id}-deps`,
            label: 'Dependencies',
            value: String(dependencies.sources.length + (dependencies.sprite ? 1 : 0) + (dependencies.glyph ? 1 : 0)),
          },
        ];

        return {
          id: style.id ?? style.name ?? 'style',
          label: style.name ?? style.id ?? 'Untitled style',
          styleIdLabel: style.id ?? null,
          description: style.description ?? null,
          shareScope: normalizeShareScope(style.share_scope),
          updatedLabel: formatDate(style.updated_at),
          sourceCountLabel: `${sourceCount} ${sourceCount === 1 ? 'source' : 'sources'}`,
          layerCountLabel: `${layerCount} ${layerCount === 1 ? 'layer' : 'layers'}`,
          runtimeStateLabel: missingCount > 0 ? 'Needs dependency review' : layerCount > 0 ? 'Preview ready' : 'Starter shell',
          statusBadges,
          summaryItems,
          dependencies,
          actions,
        };
      }),
    [filteredStyles, glyphSets, mappingAccess.featureTenantId, sourceCatalog, spriteSets],
  );

  const toolbarSummaryItems = useMemo<MapAssetStatItem[]>(() => {
    const totalStyles = filteredStyles.length;
    const totalLayers = filteredStyles.reduce((sum, style) => sum + countStyleLayers(style), 0);
    const totalSources = filteredStyles.reduce((sum, style) => sum + countStyleSources(style), 0);
    return [
      { id: 'style-count', label: 'Styles', value: String(totalStyles) },
      { id: 'layer-count', label: 'Authored Layers', value: String(totalLayers) },
      { id: 'source-refs', label: 'Source Refs', value: String(totalSources) },
    ];
  }, [filteredStyles]);

  const handleStyleAction = useCallback(
    async (styleId: string, actionId: string) => {
      const style = filteredStyles.find(candidate => candidate.id === styleId);
      if (!style) return;

      setActionError(null);
      switch (actionId) {
        case 'workbench':
          if (style.id) {
            navigate(`/maps/styles/${style.id}/workbench`);
          }
          return;
        case 'inspect':
          await openInspector(style);
          return;
        case 'duplicate':
          await handleDuplicate(style);
          return;
        case 'share':
          setShareTarget(style);
          return;
        case 'delete':
          await handleDelete(style);
          return;
        default:
          return;
      }
    },
    [filteredStyles, navigate, openInspector],
  );

  const inspectorDependencies = useMemo(
    () =>
      inspectorTarget
        ? buildDependencySummary(inspectorTarget, sourceCatalog, spriteSets, glyphSets)
        : { sources: [], sprite: null, glyph: null },
    [glyphSets, inspectorTarget, sourceCatalog, spriteSets],
  );

  const inspectorSummaryItems = useMemo<MapAssetStatItem[]>(() => {
    if (!inspectorTarget) {
      return [];
    }
    return [
      { id: 'layers', label: 'Layers', value: String(countStyleLayers(inspectorTarget)) },
      { id: 'sources', label: 'Sources', value: String(countStyleSources(inspectorTarget)) },
      { id: 'style-size', label: 'Style Size', value: getStyleJsonSizeLabel(inspectorTarget.style_json) },
    ];
  }, [inspectorTarget]);

  const inspectorRuntimeSummaryItems = useMemo<MapAssetStatItem[]>(() => {
    if (!inspectorTarget) {
      return [];
    }
    const missingCount = countMissingDependencies(inspectorDependencies, inspectorTarget, sourceCatalog, spriteSets, glyphSets);
    return [
      {
        id: 'runtime',
        label: 'Preview',
        value:
          inspectorStatus === 'success'
            ? 'Ready'
            : inspectorStatus === 'loading'
              ? 'Loading'
              : inspectorStatus === 'error'
                ? 'Failed'
                : 'Idle',
        tone:
          inspectorStatus === 'success'
            ? 'success'
            : inspectorStatus === 'error'
              ? 'danger'
              : inspectorStatus === 'loading'
                ? 'warning'
                : 'muted',
      },
      {
        id: 'missing',
        label: 'Missing',
        value: String(missingCount),
        tone: missingCount > 0 ? 'warning' : 'success',
      },
      { id: 'updated', label: 'Updated', value: formatDate(inspectorTarget.updated_at) },
    ];
  }, [glyphSets, inspectorDependencies, inspectorStatus, inspectorTarget, sourceCatalog, spriteSets]);

  const inspectorStatusBadges = useMemo<MapStyleStatusBadge[]>(() => {
    if (!inspectorTarget) return [];
    const missingCount = countMissingDependencies(inspectorDependencies, inspectorTarget, sourceCatalog, spriteSets, glyphSets);
    return [
      {
        id: 'preview-status',
        label:
          inspectorStatus === 'success'
            ? 'Preview Ready'
            : inspectorStatus === 'loading'
              ? 'Loading'
              : inspectorStatus === 'error'
                ? 'Preview Failed'
                : 'Idle',
        tone:
          inspectorStatus === 'success'
            ? 'success'
            : inspectorStatus === 'loading'
              ? 'warning'
              : inspectorStatus === 'error'
                ? 'danger'
                : 'muted',
      },
      ...(missingCount > 0 ? [{ id: 'deps', label: `${missingCount} Missing`, tone: 'warning' as const }] : []),
    ];
  }, [glyphSets, inspectorDependencies, inspectorStatus, inspectorTarget, sourceCatalog, spriteSets]);

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  return (
    <div className="space-y-6">
      <MapStyleLibraryToolbar
        title="Map Styles"
        description="Browse the shared preset library, inspect dependency readiness, and open a style in the workbench when you need deeper authoring."
        searchValue={search}
        searchPlaceholder="Search presets by name, description, or style id"
        scopeValue={shareScopeFilter}
        scopeOptions={[
          { value: 'all', label: 'All scopes' },
          { value: 'public', label: 'Public' },
          { value: 'shared', label: 'Shared' },
          { value: 'tenant', label: 'Tenant' },
        ]}
        sourceValue={sourceFilter}
        sourceOptions={sourceFilterOptions}
        sourcePlaceholder="All Sources"
        summaryItems={toolbarSummaryItems}
        primaryActionLabel="New style"
        onSearchChange={setSearch}
        onScopeChange={value => setShareScopeFilter(value as 'all' | ShareScope)}
        onSourceChange={setSourceFilter}
        onPrimaryAction={() => setSheetOpen(true)}
      />

      {actionError ? (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {actionError}
        </div>
      ) : null}

      <MapStyleLibraryTable
        styles={styleRows}
        loading={isLoading}
        errorMessage={error?.message ?? null}
        emptyTitle={data?.length ? 'No styles match this view' : 'No styles yet'}
        emptyDescription={
          data?.length
            ? 'Adjust the source, search, or scope filters to find another preset.'
            : 'Create a reusable source-compatible preset, then inspect it and open it in the workbench when needed.'
        }
        onAction={handleStyleAction}
      />

      <Sheet
        open={sheetOpen}
        onOpenChange={open => {
          setSheetOpen(open);
          if (!open) {
            resetCreateForm();
          }
        }}
      >
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Create style</SheetTitle>
            <SheetDescription>Start from a style starter and drop into the workbench when you need detailed authoring.</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <MapStyleCreateSheetBody
              title="Create Style"
              description="Prefer a starter for normal flows. Advanced JSON is available when you are importing an existing style document."
              name={createState.name}
              details={createState.details}
              shareScope={createState.shareScope}
              shareScopeOptions={[
                { value: 'public', label: 'Public', disabled: !canManageGlobal },
                { value: 'shared', label: 'Shared' },
                { value: 'tenant', label: 'Tenant' },
              ]}
              starterValue={createState.starterValue}
              starterOptions={STYLE_STARTER_OPTIONS.map(option => ({ ...option }))}
              advancedMode={createState.advancedMode}
              jsonValue={createState.jsonValue}
              errorMessage={createError}
              submitting={saving}
              onNameChange={value => setCreateState(prev => ({ ...prev, name: value }))}
              onDetailsChange={value => setCreateState(prev => ({ ...prev, details: value }))}
              onShareScopeChange={value => setCreateState(prev => ({ ...prev, shareScope: value as ShareScope }))}
              onStarterChange={value => setCreateState(prev => ({ ...prev, starterValue: value }))}
              onAdvancedModeChange={nextValue => setCreateState(prev => ({ ...prev, advancedMode: nextValue }))}
              onJsonValueChange={value => setCreateState(prev => ({ ...prev, jsonValue: value }))}
              onCancel={() => setSheetOpen(false)}
              onSubmit={handleCreate}
            />
          </div>
        </SheetContent>
      </Sheet>

      <MappingAssetAccessSheet
        open={Boolean(shareTarget)}
        onOpenChange={open => {
          if (!open) {
            setShareTarget(null);
          }
        }}
        assetTypeLabel="Map style"
        asset={shareTarget}
        featureTenantId={mappingAccess.featureTenantId}
        loadShares={assetId => listMapStyleShares(assetId)}
        saveAccess={async (asset, input) => {
          if (!asset.id) {
            throw new Error('Map style ID required');
          }
          await updateMapStyle(asset.id, { share_scope: input.shareScope });
          await replaceMapStyleShares(asset.id, input.tenantIds);
          await mutate();
        }}
      />

      <Sheet
        open={inspectorOpen}
        onOpenChange={open => {
          setInspectorOpen(open);
          if (!open) {
            setInspectorTarget(null);
            setInspectorStyleJson(null);
            setInspectorViewState(null);
            setInspectorStatus('idle');
            setInspectorError(null);
            setPreviewResetToken(0);
          }
        }}
      >
        <SheetContent side="right" size="full" className="p-0 sm:max-w-[100vw]">
          <MapStyleInspectorWorkspace
            title="Style Inspector"
            subtitle={inspectorTarget?.name ?? 'Inspect style dependencies, preview readiness, and generated style JSON.'}
            styleIdLabel={inspectorTarget?.id ?? null}
            shareScopeLabel={inspectorTarget?.share_scope ? formatShareScopeLabel(inspectorTarget.share_scope) : null}
            summaryItems={inspectorSummaryItems}
            contextPanel={
              <div className="space-y-6">
                <MapStyleDependencyPanel
                  description="Source, sprite, and glyph references that must resolve for the style preview to load correctly."
                  dependencies={inspectorDependencies}
                  summaryItems={[
                    { id: 'source-count', label: 'Sources', value: String(inspectorDependencies.sources.length) },
                    { id: 'sprite', label: 'Sprite', value: inspectorDependencies.sprite ? 'Linked' : 'None' },
                    { id: 'glyph', label: 'Glyphs', value: inspectorDependencies.glyph ? 'Linked' : 'None' },
                  ]}
                  emptyMessage="This style does not currently reference any mapped assets."
                />
                <MapStyleRuntimePanel
                  description="Preview readiness, authored density, and current asset state."
                  statusBadges={inspectorStatusBadges}
                  summaryItems={inspectorRuntimeSummaryItems}
                  body={
                    inspectorTarget ? (
                      <div className="space-y-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-muted-foreground">Asset kind</span>
                          <span className="font-semibold text-foreground">Map style</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-muted-foreground">Share scope</span>
                          <span className="font-semibold text-foreground">{formatShareScopeLabel(inspectorTarget.share_scope)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-muted-foreground">Updated</span>
                          <span className="font-semibold text-foreground">{formatDate(inspectorTarget.updated_at)}</span>
                        </div>
                      </div>
                    ) : null
                  }
                />
              </div>
            }
            previewPanel={
              <MapPreviewFrame
                title="Runtime Preview"
                description="A lightweight preview of the authored style without opening the full workbench."
                status={
                  inspectorStatus === 'error'
                    ? { label: 'Preview Failed', tone: 'danger' }
                    : inspectorStatus === 'loading'
                      ? { label: 'Loading', tone: 'warning' }
                      : inspectorStatus === 'success'
                        ? { label: 'Ready', tone: 'success' }
                        : { label: 'Idle', tone: 'muted' }
                }
                helperText="Use the workbench when you need to edit layers, rules, or interactive behavior."
                loading={inspectorStatus === 'loading'}
                errorMessage={inspectorError}
                emptyMessage={!inspectorStyleJson && !inspectorError ? 'Select a style to inspect its preview.' : null}
                toolbar={
                  <div className="flex w-full items-center justify-between gap-3">
                    <div className="text-[11px] text-muted-foreground">
                      {inspectorTarget ? `${countStyleLayers(inspectorTarget)} layers · ${countStyleSources(inspectorTarget)} sources` : 'No style selected'}
                    </div>
                    <div className="flex items-center gap-2">
                      {inspectorTarget?.id ? (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/maps/styles/${inspectorTarget.id}/workbench`)}>
                          Open workbench
                        </Button>
                      ) : null}
                      <Button variant="outline" size="sm" onClick={() => setPreviewResetToken(prev => prev + 1)}>
                        Reset view
                      </Button>
                    </div>
                  </div>
                }
                viewport={
                  inspectorStyleJson ? (
                    <StylePreviewMap
                      styleJson={inspectorStyleJson}
                      viewState={inspectorViewState}
                      resetToken={previewResetToken}
                    />
                  ) : (
                    <div className="h-full w-full bg-muted/20" />
                  )
                }
              />
            }
            diagnosticsPanel={
              <MapStyleDiagnosticsPanel
                title="Diagnostics"
                description="Inspect the last generated preview JSON and copy it for debugging."
                summaryItems={[
                  { id: 'json-size', label: 'JSON Size', value: getStyleJsonSizeLabel(inspectorStyleJson) },
                  {
                    id: 'status',
                    label: 'Preview Status',
                    value:
                      inspectorStatus === 'success'
                        ? 'Ready'
                        : inspectorStatus === 'loading'
                          ? 'Loading'
                          : inspectorStatus === 'error'
                            ? 'Failed'
                            : 'Idle',
                  },
                ]}
                generatedJson={JSON.stringify(inspectorStyleJson ?? inspectorTarget?.style_json ?? {}, null, 2)}
                helperText="The preview JSON reflects the generated runtime style, including service-side source resolution."
                onCopyJson={() =>
                  void copyText(JSON.stringify(inspectorStyleJson ?? inspectorTarget?.style_json ?? {}, null, 2))
                }
              />
            }
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
